import React, { useState } from 'react';
import { SlideLogo } from '../canvas/SlideLogo';
import { ToggleSwitch, LogoUpload, DarkenSlider, CollapseTransition } from '../../sdk';
import { getAuthHeaders } from '../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LOGO_SIZE_LEVELS = [
  { label: 'XS', value: 32 },
  { label: 'S',  value: 48 },
  { label: 'M',  value: 64 },
  { label: 'L',  value: 80 },
  { label: 'XL', value: 104 },
  { label: '2XL', value: 128 },
];

const DEFAULT_LOGO_SIZE_INDEX = 2; // M = 64px

function sizeToIndex(size) {
  if (!size) return DEFAULT_LOGO_SIZE_INDEX;
  const idx = LOGO_SIZE_LEVELS.findIndex(l => l.value === size);
  return idx >= 0 ? idx : DEFAULT_LOGO_SIZE_INDEX;
}

/**
 * Logo editor — enable/disable toggle + custom logo upload + size slider + brand name fields.
 */
export function LogoEditor({
  logoEnabled = true,
  logoUrl = null,
  logoAssetId = null,
  logoTitle = '',
  logoSubtitle = '',
  logoSize = null,
  logoIsDark = false,
  logoInvert = false,
  onLogoEnabledChange,
  onLogoChange,
  onLogoTitleChange,
  onLogoSubtitleChange,
  onLogoSizeChange,
  onLogoInvertChange,
}) {
  const [isUploading, setIsUploading] = useState(false);

  const handleFileSelect = async (file) => {
    const MAX_SIZE = 5 * 1024 * 1024;
    if (file.size > MAX_SIZE) {
      alert('Logo file must be under 5MB');
      return;
    }
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file (PNG, JPG, SVG, etc.)');
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${BACKEND_URL}/api/widgets/upload-image`, {
        method: 'POST',
        headers,
        body: formData,
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      // S3 upload returns { asset_id, download_url, is_dark }
      if (data.asset_id) {
        const isDark = data.is_dark || false;
        onLogoChange({
          logo_asset_id: data.asset_id,
          logo_url: `/api/media/asset/${data.asset_id}`,  // stable URL (PostBuilderPage strips for persistence)
          logo_is_dark: isDark,
          logo_invert: isDark,  // auto-populate toggle on upload
        });
      }
    } catch (err) {
      console.error('Logo upload failed:', err);
      alert('Failed to upload logo. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  // Has a custom logo if asset_id is set OR legacy URL is set
  const hasCustomLogo = !!logoAssetId || !!logoUrl;

  return (
    <div className="field-container">
      <div className="flex items-center justify-between">
        <div>
          <div className="module-header">Logo</div>
          <div className="module-desc">Brand mark shown on slides.</div>
        </div>
        <ToggleSwitch
          checked={logoEnabled}
          onChange={onLogoEnabledChange}
        />
      </div>
      <CollapseTransition open={logoEnabled}>
        <>
          <div className="module-rule" />
          <div className="space-y-4">
            {/* Logo preview LCD + Upload dropzone */}
            <LogoUpload
              onSelect={handleFileSelect}
              isLoading={isUploading}
              accept="image/*"
              label="Add Your Logo"
              preview={
                <SlideLogo
                  className="h-10 w-10 text-white/80"
                  logoUrl={logoUrl}
                  isDark={logoIsDark}
                />
              }
              hasCustom={hasCustomLogo}
              onReset={() => onLogoChange({
                logo_asset_id: '',
                logo_url: '',
                logo_is_dark: false,
                logo_invert: false,
              })}
            />

            {/* Size slider — discrete stepped dots */}
            <DarkenSlider
              levels={LOGO_SIZE_LEVELS}
              value={sizeToIndex(logoSize)}
              onChange={(idx) => onLogoSizeChange(LOGO_SIZE_LEVELS[idx].value)}
              label="Size"
            />

            {/* Custom logo settings — only when a custom logo is uploaded */}
            {hasCustomLogo && (
              <>
                {/* Invert Colors toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <label className="label-instrument" style={{ marginBottom: 0 }}>Invert Colors</label>
                    <div className="module-desc" style={{ marginTop: 2 }}>Light appearance on dark backgrounds.</div>
                  </div>
                  <ToggleSwitch
                    checked={logoInvert}
                    onChange={onLogoInvertChange}
                  />
                </div>

                <div>
                  <label className="label-instrument">Title</label>
                  <input
                    type="text"
                    className="input-base"
                    value={logoTitle}
                    onChange={(e) => onLogoTitleChange(e.target.value)}
                    placeholder="Brand name"
                  />
                </div>

                <div>
                  <label className="label-instrument">Subtitle</label>
                  <input
                    type="text"
                    className="input-base"
                    value={logoSubtitle}
                    onChange={(e) => onLogoSubtitleChange(e.target.value)}
                    placeholder="Tagline"
                  />
                </div>
              </>
            )}
          </div>
        </>
      </CollapseTransition>
    </div>
  );
}
